type ErrorParams<Error> = Error extends { params: infer Params } ? Params : never;

type ErrorMessage<Error> = [ErrorParams<Error>] extends [never]
    ? string
    : string | ((params: ErrorParams<Error>) => string);

export type ErrorsMap<Errors extends { code: Uppercase<string> }> = {
    [Code in Errors['code']]: ErrorMessage<Extract<Errors, { code: Code }>>;
};

/**
 * Фабрика для создания функции получения сообщения об ошибке.
 * Использовать вместе с хуком useFormFieldErrorProps.
 *
 * @kind helper
 * @param errorMap - карта ошибок (ключ - код ошибки, значение - сообщение об ошибке)
 * @typeParam Errors - тип ошибок формы
 * @returns функция для получения сообщения об ошибке
 *
 * @example
 * const featureGetErrorMessage = factoryGetErrorMessage<FeatureValidationErrors>({
 *     [VALUE_IS_REQUIRED_CODE]: VALUE_IS_REQUIRED_MESSAGE,                 // ошибки из общей библиотеки
 *     FEATURE_ALREADY_EXIST: 'Фича с таким именем уже существует',         // ошибка конкретной фичи
 *     FEATURE_LIMIT_ERROR: (params) => `Превышен лимит: ${params.limit}`,  // ошибка конкретной фичи с параметрами
 * });
 */
export const factoryGetErrorMessage =
    <Errors extends { code: Uppercase<string> }>(errorMap: ErrorsMap<Errors>) =>
    (validationError: Errors): string => {
        const { code } = validationError;
        const message = errorMap[code as keyof ErrorsMap<Errors>];

        if (typeof message === 'function' && 'params' in validationError) {
            return (message as (params: ErrorParams<Errors>) => string)(validationError.params as ErrorParams<Errors>);
        }

        return typeof message === 'string' ? message : code;
    };
